import { ReactNode, useRef } from 'react'
import { styled } from '@mui/material/styles'
import { ChevronLeftIcon } from '@weblite/icons'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import {
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import { nothing } from '$func/utils'
import { IOnboarding } from '$logic/onboarding/types'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { HotSpot } from '$common/tour/components/hot-spot'

const StyledListItem = styled(ListItem)`
  background-color: white;

  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.05);
`

const StyledListItemButton = styled(ListItemButton)`
  min-height: 50px;

  box-sizing: border-box;
  padding: 12px 20px 12px 20px;
  align-items: center;
  gap: 8px;
`

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: unset;
  flex-shrink: 0;
`

interface Props {
  text?: string
  icon: ReactNode
  onClick: () => unknown
  isLast?: boolean
  onboardingType?: IOnboarding
  doCompleteOnboarding?: boolean
  description?: string
}

export function ActionButton({
  text,
  icon,
  onClick = nothing,
  isLast = false,
  onboardingType,
  doCompleteOnboarding = true,
  description,
}: Props) {
  const ref = useRef<HTMLLIElement>(null)
  const { completeOnboarding, enabled: onboardingEnabled } =
    useConditionalHook(!!onboardingType, {
      hook: useOnboarding,
      hookArgs: onboardingType!,
    }) || {}

  return (
    <>
      <StyledListItem
        disablePadding
        sx={{ mb: 0, boxShadow: 'none' }}
        ref={ref}
      >
        <StyledListItemButton
          onClick={() => {
            onClick()
            if (doCompleteOnboarding) completeOnboarding?.()
          }}
        >
          <StyledListItemIcon>{icon}</StyledListItemIcon>
          <ListItemText
            sx={{ flexGrow: 1 }}
            primary={
              <Typography
                variant="subtitle3"
                color="neutral900"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {text}
                {onboardingEnabled && (
                  <HotSpot sx={{ mr: '8px' }} containerRef={ref} />
                )}
              </Typography>
            }
            secondary={
              description ? (
                <Typography variant="paragraph3" color="neutral800">
                  {description}
                </Typography>
              ) : undefined
            }
          />
          <ChevronLeftIcon sx={{ color: 'primary500', flexShrink: 0 }} />
        </StyledListItemButton>
      </StyledListItem>
      {!isLast && <Divider sx={{ marginRight: '54px' }} />}
    </>
  )
}
